import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const ROLES = [
  { value: 'client', label: 'Client' },
  { value: 'stylist', label: 'Stylist' },
  { value: 'partner', label: 'Partner' },
  { value: 'admin', label: 'Admin' },
];

const EMPTY = { email: '', name: '', phone: '', password: '' };

// Modal to manually create a user. Reused identically by every admin management page.
export default function AddUserDialog({ open, onOpenChange, defaultRole = 'client', onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [role, setRole] = useState(defaultRole);
  const [submitting, setSubmitting] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [copied, setCopied] = useState(false);

  // Reset all local state whenever the dialog closes so it opens fresh next time.
  const handleOpenChange = (next) => {
    if (!next) {
      setForm(EMPTY);
      setRole(defaultRole);
      setTempPassword(null);
      setCopied(false);
    }
    onOpenChange(next);
  };

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim(),
        role,
        name: form.name.trim() || undefined,
        phone: form.phone.trim() || undefined,
        password: form.password.trim() || undefined,
      };
      const res = await api.post('/admin/users', payload);
      toast.success('User added');
      onCreated?.();
      if (res.data?.temp_password) {
        setTempPassword(res.data.temp_password); // keep dialog open to surface it once
      } else {
        handleOpenChange(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add user');
    } finally {
      setSubmitting(false);
    }
  };

  const copyTemp = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the value is visible on screen to copy manually */
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" /> Add User
          </DialogTitle>
          <DialogDescription>
            Create a new account. A temporary password is generated if you leave it blank.
          </DialogDescription>
        </DialogHeader>

        {tempPassword ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              User created. Share this temporary password now — it won't be shown again:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-md bg-card border border-border px-3 py-2 text-sm font-mono break-all">
                {tempPassword}
              </code>
              <Button type="button" variant="outline" size="icon" onClick={copyTemp} aria-label="Copy password">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <DialogFooter>
              <Button type="button" className="btn-primary" onClick={() => handleOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="au-email">Email *</Label>
              <Input id="au-email" type="email" value={form.email} onChange={setField('email')}
                placeholder="name@example.com" autoFocus />
            </div>
            <div className="space-y-2">
              <Label htmlFor="au-name">Full name</Label>
              <Input id="au-name" value={form.name} onChange={setField('name')} placeholder="Jane Doe" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="au-phone">Phone (optional)</Label>
              <Input id="au-phone" value={form.phone} onChange={setField('phone')} placeholder="+64…" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="au-password">Password (optional)</Label>
              <Input id="au-password" type="text" value={form.password} onChange={setField('password')}
                placeholder="Leave blank to auto-generate" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add User'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
